import React from "react";
import { Box } from "@mui/material";
import { Draggable, Droppable, DraggableProvided, DraggableStateSnapshot } from "@hello-pangea/dnd";
import {
  PrecisionManufacturing as MachineIcon,
  PlaceOutlined,
  RepeatOutlined as RecurringIcon,
  LaunchOutlined as EscalatedIcon,
  CheckCircleOutlined,
} from "@mui/icons-material";
import { dt } from "../../../../shared/themes/redesignTokens";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { WorkOrder, WorkOrderStatusCode } from "../../models/models";
import AssigneeAvatarStack from "../shared/AssigneeAvatarStack";
import dayjs from "dayjs";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { formatDaysAgo } from "../../../../shared/utils/relativeTime";
import { formatUiDate } from "../../../../shared/utils/dateFormat";

// Priority stripe colors per spec — terracotta/amber/gray/blue palette
const PRIORITY_STRIPE_COLORS: Record<number, string> = {
  1: "#D4572B", // Critical — terracotta
  2: "#E8A817", // High — amber
  3: "#B8B5AF", // Normal — visible warm gray
  4: "#3B82F6", // Low — blue (scheduled)
};

// Priority badge label keys (rendered uppercase, per Paper mockup)
const PRIORITY_LABEL_KEYS: Record<number, string> = {
  1: "priorities.critical",
  2: "priorities.high",
  3: "priorities.normal",
  4: "priorities.low",
};

// Priority badge colors per spec (bg at 10% opacity, text color)
const PRIORITY_BADGE_COLORS: Record<number, { bg: string; text: string }> = {
  1: { bg: "rgba(212,87,43, 0.1)", text: "#D4572B" },   // Critical
  2: { bg: "rgba(232,168,23, 0.1)", text: "#C08E12" },   // High
  3: { bg: "rgba(232,229,223, 0.3)", text: "#8C9099" },   // Normal — subtle
  4: { bg: "rgba(59,130,246, 0.08)", text: "#3B82F6" },   // Low
};

interface BoardTaskCardProps {
  workOrder: WorkOrder;
  onClick: (id: string) => void;
  highlighted?: boolean;
  index: number;
  isDragEnabled: boolean;
  isCompleted?: boolean;
  isPersonDragging?: boolean;
}


const BoardTaskCard = ({
  workOrder,
  onClick,
  highlighted,
  index,
  isDragEnabled,
  isCompleted,
  isPersonDragging,
}: BoardTaskCardProps) => {
  const { isPhone } = useBreakpoint();
  const { translate, getCurrentLanguage } = useTranslation("operations");
  const priorityColor = PRIORITY_STRIPE_COLORS[workOrder.priorityCode] || PRIORITY_STRIPE_COLORS[3];
  const stripeColor = isCompleted ? "#2D8C4E" : priorityColor;  // spec: status-done green
  const priorityLabel = translate(PRIORITY_LABEL_KEYS[workOrder.priorityCode] || PRIORITY_LABEL_KEYS[3])
    .toLocaleUpperCase(getCurrentLanguage());
  const badgeColors = PRIORITY_BADGE_COLORS[workOrder.priorityCode] || PRIORITY_BADGE_COLORS[3];

  // Check if a dragged person is already assigned to this WO
  const isAlreadyAssignedCheck = (userId: string): boolean => {
    const assignees = workOrder.assignees || [];
    return assignees.some(a => a.userId === userId);
  };

  // Build assignee list for the avatar stack
  // NOTE: workOrder.assignees may be an empty array [] (not null) when the WO was
  // created with legacy assignedToUserId but no multi-assignee row yet exists.
  // Check .length > 0, not just truthiness, so the fallback actually triggers.
  const assigneeList = (workOrder.assignees && workOrder.assignees.length > 0)
    ? workOrder.assignees
    : (workOrder.assignedToUserName ? [{
        userId: workOrder.assignedToUserId || "",
        userName: workOrder.assignedToUserName,
        role: 2,
        roleName: "Lead",
        assignedAt: "",
      }] : []);

  // Compute relative time label for timestamp
  const getTimeLabel = (): { text: string; color: string } => {
    if (isCompleted) {
      return { text: translate("statuses.completed"), color: "#2D8C4E" };
    }
    // For on-hold cards, show hold duration
    if (workOrder.workOrderStatusId === WorkOrderStatusCode.OnHold) {
      const holdDays = dayjs().diff(dayjs(workOrder.modifiedDate), "day");
      return { text: translate("board.daysOnHold", { count: holdDays }), color: "#E8A817" };
    }
    // For scheduled cards with a due date, show the date
    if (workOrder.workOrderStatusId === WorkOrderStatusCode.Scheduled && workOrder.dueDate) {
      return { text: formatUiDate(workOrder.dueDate, "dayMonth"), color: "#3B82F6" };
    }
    // Default: relative time from modified date
    const days = dayjs().diff(dayjs(workOrder.modifiedDate), "day");
    return { text: formatDaysAgo(days, translate), color: "#3B82F6" };
  };

  const timeLabel = getTimeLabel();

  const cardContent = (dragSnapshot: { isDragging: boolean }, dragProvided: any) => (
    <>
      {/* Row 1: WO ID + priority badge (or green check for done) — spec 4.1 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "6px",
        }}
      >
        {/* WO number — JetBrains Mono 11px/500 #8C9099 */}
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "11px",
            fontWeight: 500,
            color: "#8C9099",
            lineHeight: "14px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {workOrder.number}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          {/* Type icons (escalated, recurring) — keep existing logic */}
          {workOrder.serviceRequestId && (
            <EscalatedIcon
              sx={{ fontSize: 12, color: "#E8A817", opacity: 0.85 }}
              titleAccess={translate("board.escalatedTooltip")}
            />
          )}
          {workOrder.recurrenceParentId && (
            <RecurringIcon sx={{ fontSize: 12, color: "#3B82F6", opacity: 0.7 }} />
          )}
          {/* Priority badge or green checkmark for done */}
          {isCompleted ? (
            <CheckCircleOutlined sx={{ fontSize: 16, color: "#2D8C4E" }} />
          ) : (
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "10px",
                fontWeight: 600,
                color: badgeColors.text,
                backgroundColor: badgeColors.bg,
                padding: "2px 8px",
                borderRadius: "4px",
                lineHeight: "12px",
                whiteSpace: "nowrap",
              }}
            >
              {priorityLabel}
            </Box>
          )}
        </Box>
      </Box>

      {/* Row 2: Title — Sora 14px/600 — spec 4.1 */}
      <Box
        sx={{
          fontFamily: dt.font,
          fontSize: "14px",
          fontWeight: 600,
          color: isCompleted ? "#4A4F57" : "#0C1117",  /* spec: text-body for done, text-primary for active */
          lineHeight: "20px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}
      >
        {workOrder.title}
      </Box>

      {/* Rows 3-4: Machine + Location block — phone: inline row, desktop/tablet: stacked column */}
      <Box sx={{
        display: "flex",
        flexDirection: isPhone ? "row" : "column",
        gap: isPhone ? "12px" : "4px",
        minWidth: 0,
      }}>
        {/* Machine icon + name */}
        {workOrder.machineName && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",      /* spec: icon-to-text gap 6px */
              minWidth: 0,
              flex: isPhone ? "1 1 0" : undefined,
              overflow: "hidden",
            }}
          >
            <MachineIcon sx={{ fontSize: 12, color: "#8C9099", flexShrink: 0 }} />
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 500,
                color: "#8C9099",   /* spec: text-muted */
                lineHeight: "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {workOrder.machineName}
            </Box>
          </Box>
        )}

        {/* Location pin icon + text */}
        {workOrder.locationName && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "6px",      /* spec: icon-to-text gap 6px */
              minWidth: 0,
              flex: isPhone ? "1 1 0" : undefined,
              overflow: "hidden",
            }}
          >
            <PlaceOutlined sx={{ fontSize: 12, color: "#D4572B", flexShrink: 0 }} />
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "11px",
                fontWeight: 500,
                color: "#8C9099",   /* spec: text-muted */
                lineHeight: "14px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {workOrder.parentLocationName
                ? `${workOrder.locationName}, ${workOrder.parentLocationName}`
                : workOrder.locationName}
            </Box>
          </Box>
        )}
      </Box>

      {/* Row 5: Assignee avatar + name ... timestamp — spec 4.1 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "22px",
          marginTop: "auto",
        }}
      >
        {/* Assignee avatar stack + name */}
        <AssigneeAvatarStack
          assignees={assigneeList}
          maxVisible={3}
          size={22}
        />

        {/* Timestamp — JetBrains Mono 10px/400 #8C9099 (or colored for specific states) */}
        <Box
          sx={{
            fontFamily: dt.fontMono,
            fontSize: "10px",
            fontWeight: 400,
            color: timeLabel.color,
            lineHeight: "12px",
            flexShrink: 0,
            whiteSpace: "nowrap",
          }}
        >
          {timeLabel.text}
        </Box>
      </Box>
    </>
  );

  // Render the visual card (used both in normal flow and in portal while dragging)
  const renderCard = (
    provided: DraggableProvided,
    snapshot: DraggableStateSnapshot,
    dropProvided: any,
    dropSnapshot: any,
    alreadyAssigned: boolean
  ) => (
    <Box
      ref={dropProvided.innerRef}
      {...dropProvided.droppableProps}
      onClick={() => {
        if (!snapshot.isDragging) onClick(workOrder.id);
      }}
      sx={{
        position: "relative",
        display: "flex",
        borderRadius: "10px",       /* spec: card border-radius 10px */
        overflow: "hidden",
        backgroundColor: isCompleted ? "rgba(255,255,255,0.6)" : "#FFFFFF",  /* spec: card-bg / card-bg-done */
        boxShadow: dropSnapshot.isDraggingOver && !alreadyAssigned
          ? "0 0 12px rgba(52, 152, 219, 0.3)"
          : snapshot.isDragging
            ? "0 8px 24px rgba(0,0,0,0.12)"
            : "none",
        cursor: isDragEnabled ? "grab" : "pointer",
        transition: snapshot.isDragging ? "none" : "all 0.2s ease",
        transform: snapshot.isDragging ? "rotate(2deg) scale(1.02)" : "none",
        ...(highlighted && !snapshot.isDragging && { animation: "rowHighlight 1.5s ease-out" }),
        "&:hover": snapshot.isDragging
          ? {}
          : {
              backgroundColor: isCompleted ? "rgba(255,255,255,0.7)" : "#F7F5F0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            },
      }}
    >
      {/* Priority stripe — 4px wide, full height, left side — spec 4.1 */}
      <Box
        sx={{
          width: "4px",
          flexShrink: 0,
          backgroundColor: dropSnapshot.isDraggingOver
            ? alreadyAssigned ? "#9CA3AF" : "#3498DB"
            : stripeColor,
        }}
      />

      {/* Card content area */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: isCompleted ? "8px" : "10px",     /* spec: 10px active, 8px done */
          padding: "14px 16px",                   /* spec: card content padding */
          minWidth: 0,
          ...(dropSnapshot.isDraggingOver && !alreadyAssigned && {
            outline: "2px solid #3498DB",
            outlineOffset: "-2px",
            borderRadius: "0 10px 10px 0",
          }),
        }}
      >
        {cardContent(snapshot, provided)}
      </Box>

      {/* Hidden placeholder for the droppable (person/team drops don't produce children) */}
      <Box sx={{ display: "none" }}>{dropProvided.placeholder}</Box>
    </Box>
  );

  return (
    <Draggable
      draggableId={`wo-${workOrder.id}`}
      index={index}
      isDragDisabled={!isDragEnabled}
    >
      {(provided, snapshot) => {
        // Merge drag styles with high z-index when dragging (Bug 2 fix)
        const draggableStyle = {
          ...provided.draggableProps.style,
          ...(snapshot.isDragging && { zIndex: 9999 }),
        };

        const draggableElement = (
          <Box
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...(isDragEnabled ? provided.dragHandleProps : {})}
            style={{
              ...draggableStyle,
              ...(isDragEnabled && { touchAction: "none" }),
            }}
          >
            {/* Wrap card in a Droppable to receive person/team drags */}
            <Droppable droppableId={`card-${workOrder.id}`} type="PERSON">
              {(dropProvided, dropSnapshot) => {
                // Determine if the dragged item is already assigned
                const dragId = dropSnapshot.draggingOverWith || "";
                const isPersonDrag = dragId.startsWith("person::") || dragId.startsWith("team-");
                const isMachineDrag = dragId.startsWith("machine::");
                const isLocationDrag = dragId.startsWith("location::");

                let alreadyAssigned = false;
                if (isPersonDrag && dragId.startsWith("person::")) {
                  const parts = dragId.split("::");
                  const userId = parts[2];
                  alreadyAssigned = isAlreadyAssignedCheck(userId);
                } else if (isMachineDrag) {
                  const machineId = dragId.split("::")[1];
                  alreadyAssigned = workOrder.machineId === machineId;
                } else if (isLocationDrag) {
                  const locationId = dragId.split("::")[1];
                  alreadyAssigned = workOrder.locationId === locationId;
                }

                return renderCard(provided, snapshot, dropProvided, dropSnapshot, alreadyAssigned);
              }}
            </Droppable>
          </Box>
        );

        return draggableElement;
      }}
    </Draggable>
  );
};

export default BoardTaskCard;
